import { Conditions, conditionsHasId, conditionsSize, copyConditions, getConditionById, getConditionByIndex, getConditionIds, updateConditionsAndAdd } from "utils/insight/conditions";
import { isBinaryOutcome } from "utils/insight/metadata";
import { getDefaultNumericComparator, keysOf, range, rangeWithStart, rangeWithStartStep1 } from "utils/collect";
export class SummaryFactory {
    summaries;
    _maxOrder = undefined;
    constructor(summaries) {
        this.summaries = summaries;
    }
    copy() {
        return new SummaryFactory({ ...this.summaries });
    }
    getAllIds() {
        const ids = Object.values(this.summaries).flatMap((s) => s.getIds());
        return [...new Set(ids)];
    }
    hasSummary(ids) {
        return this.summaries[SummaryFactory._encodeIds(ids)] !== undefined;
    }
    getSummary(conditions) {
        const set = this._getSummarySet(getConditionIds(conditions));
        if (set === undefined) {
            return undefined;
        }
        return set.getSummary(conditions);
    }
    getSummarySet(ids) {
        const set = this._getSummarySet(ids);
        if (set === undefined) {
            return new DefaultSummarySet(new IndexProvider([], []), []);
        }
        return set;
    }
    getCombinationCount() {
        return Object.values(this.summaries).reduce((acc, summary) => {
            return acc + summary.size();
        }, 0);
    }
    getMaxOrder() {
        if (this._maxOrder !== undefined) {
            return this._maxOrder;
        }
        this._maxOrder = Math.max(...Object.values(this.summaries).map((s) => s.getIds().length));
        return this._maxOrder;
    }
    update(summaries) {
        for (const summary of summaries) {
            this.add(summary);
        }
    }
    add(summary) {
        this._maxOrder = undefined;
        this.summaries[SummaryFactory._encodeIds(summary.getIds())] = summary;
    }
    _getSummarySet(ids) {
        return this.summaries[SummaryFactory._encodeIds(ids)];
    }
    static _encodeIds(ids) {
        ids = ids.toSorted(getDefaultNumericComparator());
        switch (ids.length) {
            case 0:
                return "";
            case 1:
                return ids[0].toString();
            case 2:
                if (ids[0] < ids[1]) {
                    return ids[0].toString() + "-" + ids[1].toString();
                }
                else {
                    return ids[1].toString() + "-" + ids[0].toString();
                }
            default:
                return JSON.stringify(ids);
        }
    }
    addTables(tables, metadata) {
        const outcome = metadata.getOutcome();
        const flipOutcomeSuccess = isBinaryOutcome(outcome) &&
            tables.target.success_value !== undefined &&
            tables.target.success_value !== outcome.getSuccessValue();
        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let i = 0; i < tables.tables.length; i++) {
            this.add(createFromTable(metadata, tables.tables[i], flipOutcomeSuccess));
        }
        return this;
    }
    static create(tables, metadata) {
        const outcome = metadata.getOutcome();
        const flipOutcomeSuccess = isBinaryOutcome(outcome) &&
            tables.target.success_value !== undefined &&
            tables.target.success_value !== outcome.getSuccessValue();
        const summaryMap = {};
        const constantTables = {};
        const firstOrderTables = {};
        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let i = 0; i < tables.tables.length; i++) {
            const table = tables.tables[i];
            if (table.ids.length == 1) {
                const id = table.ids[0];
                firstOrderTables[id] = table;
                if (table.n.length == 1) {
                    constantTables[id] = table;
                }
            }
            const set = createFromTable(metadata, table, flipOutcomeSuccess);
            summaryMap[SummaryFactory._encodeIds(set.getIds())] = set;
        }
        for (let i = 0; i < tables.columns.length; i++) {
            const key = SummaryFactory._encodeIds([i]);
            if (summaryMap[key] !== undefined) {
                continue;
            }
            for (let j = 0; j < tables.columns.length && summaryMap[key] === undefined; j++) {
                if (i != j) {
                    const pair = [i, j];
                    const set = summaryMap[SummaryFactory._encodeIds(pair)];
                    if (set !== undefined) {
                        const levelCount = metadata.getColumn(i).getLevelCount();
                        const indexProvider = new IndexProvider([i], [levelCount]);
                        const summaries = set
                            ? range(levelCount)
                                .map((value) => Conditions.single(i, value))
                                .map((c) => Summary.addAll(set.getSummaries(c), c))
                            : [];
                        summaryMap[key] = new DefaultSummarySet(indexProvider, summaries);
                        break;
                    }
                }
            }
        }
        for (const i of keysOf(constantTables)) {
            for (let j = 0; j < tables.columns.length; j++) {
                const firstOrderTable = firstOrderTables[j];
                if (i == j || firstOrderTable === undefined) {
                    continue;
                }
                const ids = [i, j];
                ids.sort();
                const table = {
                    ...firstOrderTable,
                    ids: ids
                };
                const set = createFromTable(metadata, table, flipOutcomeSuccess);
                summaryMap[SummaryFactory._encodeIds(set.getIds())] = set;
            }
        }
        const set = summaryMap[SummaryFactory._encodeIds([0])];
        const indexProvider = new IndexProvider([], [1]);
        const c = Conditions.emptySet();
        const summaries = set ? [Summary.addAll(set.getSummaries(c), c)] : [];
        summaryMap[SummaryFactory._encodeIds([])] = new DefaultSummarySet(indexProvider, summaries);
        return new SummaryFactory(summaryMap);
    }
}
class AbstractSummarySet {
    indexProvider;
    constructor(indexProvider) {
        this.indexProvider = indexProvider;
    }
    size() {
        return this.indexProvider.getSize();
    }
    getIds() {
        return this.indexProvider.getIds();
    }
    getSummary(conditions) {
        return this.getSummaryByIndex(this.indexProvider.getIndex(conditions));
    }
    getSummaries(conditions) {
        const indices = this.indexProvider.getIndices(conditions);
        if (indices.length == 1) {
            if (indices[0] == -1 || indices[0] >= this.indexProvider.getSize()) {
                return [];
            }
            return [this.getSummaryByIndex(indices[0])];
        }
        const summaries = new Array(indices.length);
        for (let i = 0; i < indices.length; i++) {
            summaries[i] = this.getSummaryByIndex(indices[i]);
        }
        return summaries;
    }
}
class DefaultSummarySet extends AbstractSummarySet {
    summaries;
    constructor(indexProvider, summaries) {
        super(indexProvider);
        this.summaries = summaries;
    }
    getSummaryByIndex(index) {
        return this.summaries[index];
    }
}
class TypedArraySummarySet extends AbstractSummarySet {
    n;
    sum;
    sum_2;
    constructor(indexProvider, n, sum, sum_2) {
        super(indexProvider);
        this.n = n;
        this.sum = sum;
        this.sum_2 = sum_2;
    }
    getSummaryByIndex(index) {
        return new Summary(this.indexProvider.getConditions(index), this.n[index], 0, this.sum[index], this.sum_2[index]);
    }
}
class TypedArraySummarySetWithNulls extends AbstractSummarySet {
    n;
    n_null;
    sum;
    sum_2;
    constructor(indexProvider, n, n_null, sum, sum_2) {
        super(indexProvider);
        this.n = n;
        this.n_null = n_null;
        this.sum = sum;
        this.sum_2 = sum_2;
    }
    getSummaryByIndex(index) {
        return new Summary(this.indexProvider.getConditions(index), this.n[index], this.n_null[index], this.sum[index], this.sum_2[index]);
    }
}
function flipTable(table) {
    const sum = new Array(table.n.length);
    for (let i = 0; i < table.n.length; i++) {
        sum[i] = table.n[i] - table.sum[i];
    }
    return {
        ids: table.ids,
        n: table.n,
        n_null: table.n_null,
        sum: sum,
        sum_2: sum
    };
}
function createFromTable(metadata, table, flipOutcomeSuccess) {
    const indexProvider = new IndexProvider(table.ids, table.ids.map((id) => metadata.getColumn(id).getLevelCount()));
    if (flipOutcomeSuccess) {
        table = flipTable(table);
    }
    if (table.n_null !== undefined) {
        return new TypedArraySummarySetWithNulls(indexProvider, table.n, table.n_null, table.sum, table.sum_2 === undefined ? table.sum : table.sum_2);
    }
    return new TypedArraySummarySet(indexProvider, table.n, table.sum, table.sum_2 === undefined ? table.sum : table.sum_2);
}
export class IndexProvider {
    ids;
    dimensions;
    _size;
    constructor(ids, dimensions) {
        this.ids = ids;
        this.dimensions = dimensions;
    }
    getIds() {
        return this.ids;
    }
    getSize() {
        if (this._size !== undefined) {
            return this._size;
        }
        if (this.dimensions.length == 0) {
            this._size = 0;
            return 0;
        }
        this._size = this.dimensions[0];
        if (this.dimensions.length == 1) {
            return this._size;
        }
        else if (this.dimensions.length == 2) {
            this._size *= this.dimensions[1];
        }
        else {
            for (let i = 1; i < this.dimensions.length; i++) {
                this._size *= this.dimensions[i];
            }
        }
        return this._size;
    }
    getIndex(conditions) {
        let index = 0;
        let condition;
        for (let i = 0; i < this.ids.length; i++) {
            condition = getConditionById(conditions, this.ids[i]);
            if (condition === undefined) {
                return -1;
            }
            index = index * this.dimensions[i] + condition.value;
        }
        return index;
    }
    getIndices(conditions) {
        if (conditions === undefined) {
            return range(this.getSize());
        }
        const n = conditionsSize(conditions);
        if (n == 0) {
            return range(this.getSize());
        }
        else if (this.ids.length == 0) {
            return [0];
        }
        else if (this.ids.length == 1) {
            const c = n == 1 ? getConditionByIndex(conditions, 0) : getConditionById(conditions, this.ids[0]);
            if (c !== undefined && c.id == this.ids[0]) {
                return [c.value];
            }
            else {
                return range(this.getSize());
            }
        }
        else if (this.ids.length == 2) {
            if (n < 3) {
                const first = getConditionByIndex(conditions, 0);
                if (first == undefined) {
                    return range(this.getSize());
                }
                const second = getConditionByIndex(conditions, 1);
                if (second == undefined) {
                    if (first.id == this.ids[0]) {
                        const start = this.dimensions[1] * first.value;
                        return rangeWithStartStep1(start, start + this.dimensions[1]);
                    }
                    else if (first.id == this.ids[1]) {
                        return rangeWithStart(first.value, this.getSize(), this.dimensions[1]);
                    }
                    else {
                        return range(this.getSize());
                    }
                }
                else if (first.id == this.ids[0] && second.id == this.ids[1]) {
                    return [this.getIndex(conditions)];
                }
            }
        }
        for (let i = 0; i < this.ids.length; i++) {
            if (!conditionsHasId(conditions, this.ids[i])) {
                const child = copyConditions(conditions);
                let children = [];
                let index;
                for (let value = 0; value < this.dimensions[i]; value++) {
                    if (index === undefined) {
                        index = updateConditionsAndAdd(child, Conditions.create(this.ids[i], value));
                    }
                    else {
                        child[index] = Conditions.create(this.ids[i], value);
                    }
                    children = children.concat(this.getIndices(child));
                }
                return children;
            }
        }
        return [this.getIndex(conditions)];
    }
    getConditions(index) {
        const conditions = new Array(this.ids.length);
        let value;
        for (let i = this.ids.length; i-- > 0;) {
            value = index % this.dimensions[i];
            index -= value;
            index /= this.dimensions[i];
            conditions[i] = { id: this.ids[i], value: value };
        }
        return conditions;
    }
}
export class Summary {
    conditions;
    n;
    missingN;
    sum;
    sumOfSquares;
    constructor(conditions, n, missingN, sum = n, sumOfSquares = sum) {
        this.conditions = conditions;
        this.n = n;
        this.missingN = missingN;
        this.sum = sum;
        this.sumOfSquares = sumOfSquares;
    }
    copyWithConditions(conditions) {
        return new Summary(conditions, this.n, this.missingN, this.sum, this.sumOfSquares);
    }
    getMean() {
        return this.sum / this.n;
    }
    getN() {
        return this.n;
    }
    getMissingN() {
        return this.missingN ?? 0;
    }
    getTotalN() {
        return this.getN() + this.getMissingN();
    }
    getStandardDeviation() {
        return Math.sqrt(this.getVariance());
    }
    getSum() {
        return this.sum;
    }
    getSumOfSquares() {
        return this.sumOfSquares;
    }
    getTotalSumOfSquares() {
        return this.n === 0 ? 0 : this.sumOfSquares - (this.sum * this.sum) / this.n;
    }
    getVariance() {
        return this.getTotalSumOfSquares() / (this.n - 1);
    }
    subtract(summary) {
        if (summary.n == 0) {
            return this;
        }
        const n = this.n - summary.getN();
        const missingN = this.missingN === undefined ? this.missingN : this.getMissingN() - summary.getMissingN();
        const sum = this.sum - summary.getSum();
        const sumOfSquares = this.sumOfSquares - summary.getSumOfSquares();
        if (this.sum == this.sumOfSquares && summary.getSum() == summary.getSumOfSquares()) {
            return new Summary(this.conditions, Math.max(n, 0), missingN !== undefined ? Math.max(missingN, 0) : undefined, Math.max(sum, 0), Math.max(sumOfSquares, 0));
        }
        else if (n <= 0) {
            return new Summary(this.conditions, 0, 0, 0, 0);
        }
        else {
            return new Summary(this.conditions, n, missingN, sum, sumOfSquares);
        }
    }
    add(summary, conditions) {
        if (summary.getTotalN() == 0) {
            return this;
        }
        return Summary.addAll([this, Summary.from(summary)], conditions ?? this.conditions);
    }
    static addAll(summaries, conditions) {
        let n = 0, nMissing = 0, sum = 0, sumOfSquares = 0;
        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let i = 0; i < summaries.length; i++) {
            n += summaries[i].n;
            nMissing += summaries[i].getMissingN();
            sum += summaries[i].sum;
            sumOfSquares += summaries[i].sumOfSquares;
        }
        if (summaries.length > 0 && summaries[0].missingN === undefined) {
            nMissing = undefined;
        }
        return new Summary(conditions ?? Conditions.emptySet(), n, nMissing, sum, sumOfSquares);
    }
    static from(summary) {
        return new Summary(Conditions.emptySet(), summary.getN(), summary.getMissingN(), summary.getSum(), summary.getSumOfSquares());
    }
}
